import React, { useEffect, useState, useCallback } from 'react';
import { getStaffingRequests, addStaffingRequest, updateStaffingRequest, deleteStaffingRequest } from '@/services/schedulingService';
import PageHeading from '@/components/common/PageHeading';
import LoadingOverlay from '@/components/common/LoadingOverlay';
import COLUMNS from './requestColumns';

const emptyRow = () => ({
  Owner: '',
  CreationDate: new Date().toISOString().split('T')[0],
  Client: '',
  Project: '',
  Level: '',
  StartDate: '',
  EndDate: '',
  Type: '',
  Name: '',
  ContractorAllowed: false,
  Adjudicated: false,
  Closed: false,
  RC: '',
  Comments: ''
});

const formatDate = v => {
  if (!v) return '';
  const d = new Date(v);
  if (isNaN(d.getTime())) return '';
  return d.toISOString().split('T')[0];
};

const s = {
  container: { padding: '28px', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 },
  title: { fontSize: 22, fontWeight: 600, color: '#1a1a1a', margin: 0 },
  addBtn: {
    background: '#25634b',
    color: '#fff',
    border: 'none',
    borderRadius: 4,
    padding: '8px 18px',
    fontWeight: 600,
    cursor: 'pointer',
    fontSize: 14
  },
  tableWrap: { overflowX: 'auto', border: '1px solid #e0e0e0', borderRadius: 6 },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 11, tableLayout: 'fixed' },
  th: {
    background: '#f0f4f0',
    color: '#53665a',
    padding: '6px 4px',
    textAlign: 'left',
    fontWeight: 600,
    fontSize: 11,
    position: 'sticky',
    top: 0,
    verticalAlign: 'bottom'
  },
  thBool: {
    background: '#f0f4f0',
    color: '#53665a',
    padding: '6px 2px',
    textAlign: 'center',
    fontWeight: 600,
    fontSize: 11,
    position: 'sticky',
    top: 0,
    verticalAlign: 'bottom'
  },
  filterInput: {
    width: '100%',
    padding: '3px 4px',
    border: '1px solid #dbe5dc',
    borderRadius: 2,
    fontSize: 10,
    boxSizing: 'border-box',
    background: '#fff',
    color: '#20332c',
    marginTop: 3
  },
  filterSelect: {
    width: '100%',
    padding: '3px 2px',
    border: '1px solid #dbe5dc',
    borderRadius: 2,
    fontSize: 10,
    boxSizing: 'border-box',
    background: '#fff',
    color: '#20332c',
    marginTop: 3
  },
  td: { padding: '2px 3px', borderBottom: '1px solid #eee', verticalAlign: 'middle', overflow: 'hidden' },
  tdBool: { padding: '2px 2px', borderBottom: '1px solid #eee', verticalAlign: 'middle', textAlign: 'center' },
  input: {
    width: '100%',
    padding: '4px 4px',
    border: '1px solid #ddd',
    borderRadius: 3,
    fontSize: 11,
    boxSizing: 'border-box',
    background: '#fff'
  },
  dateInput: {
    width: '100%',
    padding: '4px 2px',
    border: '1px solid #ddd',
    borderRadius: 3,
    fontSize: 10,
    boxSizing: 'border-box',
    background: '#fff'
  },
  checkbox: { width: 15, height: 15, cursor: 'pointer' },
  deleteBtn: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: '#c00',
    fontSize: 15,
    padding: '2px 4px',
    borderRadius: 3
  }
};

const RequestsPage = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [filters, setFilters] = useState({});
  const [hideClosed, setHideClosed] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const data = await getStaffingRequests();
      setRows(
        data.map(r => ({
          ...r,
          CreationDate: formatDate(r.CreationDate),
          StartDate: formatDate(r.StartDate),
          EndDate: formatDate(r.EndDate),
          ContractorAllowed: !!r.ContractorAllowed,
          Adjudicated: !!r.Adjudicated,
          Closed: !!r.Closed
        }))
      );
    } catch (e) {
      console.error('Error fetching staffing requests:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const filteredRows = rows.filter(row => {
    if (hideClosed && row.Closed) return false;
    return COLUMNS.every(col => {
      const f = filters[col.key];
      if (f === undefined || f === '') return true;
      if (col.type === 'boolean') {
        if (f === 'all') return true;
        return f === 'yes' ? !!row[col.key] : !row[col.key];
      }
      const val = String(row[col.key] || '').toLowerCase();
      return val.includes(f.toLowerCase());
    });
  });

  const handleAdd = async () => {
    const newRow = emptyRow();
    try {
      const result = await addStaffingRequest(newRow);
      const insertId = result[0] || result.insertId || result;
      setRows(prev => [...prev, { ...newRow, RequestID: insertId }]);
    } catch (e) {
      console.error('Error adding row:', e);
    }
  };

  const handleDelete = async id => {
    try {
      await deleteStaffingRequest(id);
      setRows(prev => prev.filter(r => r.RequestID !== id));
    } catch (e) {
      console.error('Error deleting row:', e);
    }
  };

  const handleChange = (id, key, value) => {
    setRows(prev => prev.map(r => (r.RequestID === id ? { ...r, [key]: value } : r)));
    const timeoutKey = `${id}-${key}`;
    if (saving[timeoutKey]) clearTimeout(saving[timeoutKey]);
    const timeout = setTimeout(async () => {
      try {
        await updateStaffingRequest(id, { [key]: value });
      } catch (e) {
        console.error('Error saving:', e);
      }
    }, 500);
    setSaving(prev => ({ ...prev, [timeoutKey]: timeout }));
  };

  return (
    <div style={s.container}>
      {loading && <LoadingOverlay />}
      <PageHeading title='Staffing requests'>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#444', cursor: 'pointer', userSelect: 'none' }}>
            <input type='checkbox' checked={hideClosed} onChange={e => setHideClosed(e.target.checked)} style={{ width: 16, height: 16, cursor: 'pointer' }} />
            Hide closed
          </label>
          <button className='workspace-button' onClick={handleAdd}>
            + Add Request
          </button>
        </div>
      </PageHeading>
      <div style={s.tableWrap}>
        <table style={s.table}>
          <colgroup>
            {COLUMNS.map(col => (
              <col key={col.key} style={col.width ? { width: col.width } : undefined} />
            ))}
            <col style={{ width: 28 }} />
          </colgroup>
          <thead>
            <tr>
              {COLUMNS.map(col => (
                <th key={col.key} style={col.type === 'boolean' ? s.thBool : s.th}>
                  <div>{col.label}</div>
                  {col.type === 'boolean' ? (
                    <select style={s.filterSelect} value={filters[col.key] || 'all'} onChange={e => setFilters(prev => ({ ...prev, [col.key]: e.target.value }))}>
                      <option value='all'>All</option>
                      <option value='yes'>Yes</option>
                      <option value='no'>No</option>
                    </select>
                  ) : (
                    <input type='text' placeholder='Filter...' style={s.filterInput} value={filters[col.key] || ''} onChange={e => setFilters(prev => ({ ...prev, [col.key]: e.target.value }))} />
                  )}
                </th>
              ))}
              <th style={{ ...s.th, width: 28 }}></th>
            </tr>
          </thead>
          <tbody>
            {filteredRows.map(row => (
              <tr key={row.RequestID}>
                {COLUMNS.map(col => (
                  <td key={col.key} style={col.type === 'boolean' ? s.tdBool : s.td}>
                    {col.type === 'boolean' ? (
                      <input type='checkbox' checked={!!row[col.key]} onChange={e => handleChange(row.RequestID, col.key, e.target.checked)} style={s.checkbox} />
                    ) : col.type === 'date' ? (
                      <input type='date' value={row[col.key] || ''} onChange={e => handleChange(row.RequestID, col.key, e.target.value)} style={s.dateInput} />
                    ) : (
                      <input type='text' value={row[col.key] || ''} onChange={e => handleChange(row.RequestID, col.key, e.target.value)} style={s.input} />
                    )}
                  </td>
                ))}
                <td style={s.td}>
                  <button style={s.deleteBtn} onClick={() => handleDelete(row.RequestID)} title='Delete row'>
                    &times;
                  </button>
                </td>
              </tr>
            ))}
            {filteredRows.length === 0 && !loading && (
              <tr>
                <td colSpan={COLUMNS.length + 1} style={{ ...s.td, textAlign: 'center', padding: 40, color: '#999' }}>
                  {rows.length === 0 ? 'No requests yet. Click "+ Add Request" to create one.' : 'No rows match the current filters.'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default RequestsPage;
